<!-- eslint-disable vue/no-v-html -->
<template>
  <Layout>
    <PageHeader :title="title" :sub-title="subTitle" />
    <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
      <div class="float-end">
        <McButton
          variant="secondary"
          :text="$t('global.setting')"
          icon="icon-outline_settings"
          @click="$router.push({ name: 'Setting Notification List' })"
        />
      </div>
    </div>
    <div
      v-if="hasAccessAll(['alert_rule_template:read', 'alert_rule_template:create'])"
      class="row"
    >
      <div class="col-md-4 col-sm-12">
        <div class="card border border-1">
          <div class="mc-btn__recommendation">
            <img :src="illustrationPath" alt="Recommendation Notification" />
            <div>
              <h5>{{ $t('notification.onboarding.notSure') }}</h5>
              <span class="text-muted d-block">
                {{ $t('notification.onboarding.checkRecommendation') }}
              </span>
              <McButton
                :text="$t('notification.onboarding.seeRecommendation')"
                size="sm"
                class="mt-2"
                @click="showOnboarding"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <McGrapqlTable
      v-if="notifications.length > 0"
      :apiClient="query"
      :fields="fields"
      :activeFilters="activeFilters"
      trackBy="id"
      :hasDelete="false"
      :rowClass="unreadNotification"
      :refreshData="!reload"
      :hasSelectAll="false"
      :hasCheckbox="false"
      :isLoading="isLoading"
      hasFilter
      searchable
      withCard
      :class="hasAccess(['e4e-alert-center-urgent:read']) ? '' : 'mc-table__notification-important'"
      :initialPage="currentPage"
      :initialLimit="currentLimit"
      :initialSearch="currentSearch"
      @handleSelectValue="handleSelected"
      @update:page="handleTableUpdate('page', $event)"
      @update:limit="handleTableUpdate('limit', $event)"
      @update:search="handleTableUpdate('search', $event)"
    >
      <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataFields"
          @setColumn="handleSetDataFields"
          @setField="handleFieldShow"
        />
      </template>
      <template #button-filter>
        <div class="d-flex justify-content-between">
          <div class="d-flex align-items-center">
            <div class="form-check form-switch form-switch-md mb-0">
              <input
                v-model="isUrgentImportant"
                class="form-check-input"
                type="checkbox"
                @change="handleImportant"
              />
              {{
                hasAccess(['e4e-alert-center-urgent:read'])
                  ? $t('notification.text.urgentImportant')
                  : $t('notification.text.onlyImportant')
              }}
            </div>
          </div>
          <div class="d-flex gap-2">
            <div class="mc-filter-date-picker">
              <FormulateInput
                id="period"
                v-model="period"
                name="period"
                isShow="true"
                :placeholder="`${$t('global.form.select')} ${$t('global.field.date')}`"
                type="date-picker"
                datepickerType="date"
                valueType="YYYY-MM-DD"
                format="DD MMM YYYY"
                titleFormat="DD MMM YYYY"
                rangeSeparator=" - "
                :range="true"
                :confirm="true"
                :draft="true"
                :confirmText="$t('global.action.apply')"
                :shortcuts="makeShortcuts"
                validation="required"
                :validation-messages="{
                  required: $t('maintenance.validations.required'),
                }"
                :clearable="false"
                :disabled-date="disabledRange"
                :value="period"
                @pick="pickDate"
                @close="closePicker"
                @input="applyDateFilters"
              />
              <b-tooltip
                target="period"
                placement="left"
                :title="$t('notification.text.adjustFilterDate')"
              />
            </div>

            <div class="pt-1">
              <ModalFilter
                :activeFilters="filterModal"
                :alertCode="getAlertOptions"
                :setFullObjects="vehicleObject"
                @applyFilters="handleApplyFilters"
                @applyFullObjects="handleLocalFullObjects"
              />
            </div>
          </div>
        </div>
      </template>
      <template #cell(alert)="data">
        <a
          class="mc-table__clickable-field"
          :href="getNotificationUrl(data.item)"
          @click="rowClicked(data.item)"
        >
          <span
            v-if="
              data.item.isImportant ||
              (data.item.isUrgent && hasAccess(['e4e-alert-center-urgent:read']))
            "
          >
            <span
              :class="
                (data.item.isImportant && !hasAccess(['e4e-alert-center-urgent:read'])) ||
                (data.item.isUrgent && hasAccess(['e4e-alert-center-urgent:read']))
                  ? `${
                      'urgent-icon' +
                      (hasRuleNameScope && data.item.alertRuleIds?.length > 0 ? ' counter' : '')
                    }`
                  : `${
                      'important-icon' +
                      (hasRuleNameScope && data.item.alertRuleIds?.length > 0 ? ' counter' : '')
                    }`
              "
            >
              <i v-if="data.item.alertRuleIds?.length > 0 && hasRuleNameScope">{{
                data.item.alertRuleIds?.length
              }}</i>
              <img
                src="https://api-vsms-v2-bucket.s3.ap-southeast-1.amazonaws.com/assets/alert_icon/alert_triangle_20241119.svg"
                :alt="data.value.descriptionIdn"
              />
            </span>
          </span>
          <div
            :class="
              'mc-notification-log_notification-name' +
              (data.item.isImportant ||
              (data.item.isUrgent && hasAccess(['e4e-alert-center-urgent:read']))
                ? '__important'
                : '')
            "
          >
            <McBadge
              v-if="isFO(data.item.alert?.alertCategories, data.item.alertCode)"
              class="me-1"
              :text="data.item.alertData?.includes('|') ? data.item.alertData?.split('|')[0] : ''"
              type="button"
              variant="outlineSuccess"
            />
            <span v-if="hasRuleNameScope" class="d-flex flex-column">
              <span>
                {{
                  data.item.alertRule
                    ? data.item.alertRule.label
                    : getNotificationDescription(data.item, data.value)
                }}
              </span>
              <span v-if="data.item.alertRule" class="text-muted">
                {{ getNotificationDescription(data.item, data.value) }}
              </span>
            </span>
            <span v-else>
              {{ getNotificationDescription(data.item, data.value) }}
            </span>
          </div>
          <div>
            <i
              v-b-tooltip.hover="
                `${$t('notification.text.sourceDevice')}: ${
                  data.item.sourceDeviceType === 'CAM' ? $t('trackVideo.camera') : 'GPS'
                }`
              "
              :class="{
                'mc-fa icon-outline_video ms-2 align-middle': data.item.sourceDeviceType === 'CAM',
                'mc-fa icon-outline_device ms-2 align-middle': data.item.sourceDeviceType === 'GPS',
              }"
            ></i>
          </div>
        </a>
      </template>
      <template #cell(media)="data">
        <McButton
          v-if="
            data.item.mediaStatus === 'Success' ||
            checkIsAlertRequested(data.item) ||
            checkIsAutoUploadSuccessfull(data.item) === true
          "
          icon="icon-outline_image"
          :text="$t('global.media')"
          variant="outline-primary"
          size="small"
          :disabled="!isHaveMediaMultiImei(data.item)"
          :displayTooltip="!isHaveMediaMultiImei(data.item)"
          :tooltipText="$t('global.noMedia')"
          @click="openModalMedia(data.item)"
        />
        <McButton
          v-else-if="
            data.item.mediaStatus === 'RequestMedia' ||
            checkIsAutoUploadSuccessfull(data.item) === false
          "
          icon="icon-outline_download"
          :text="'Request Media'"
          variant="outline-primary"
          size="small"
          :disabled="!isHaveMediaMultiImei(data.item)"
          :displayTooltip="!isHaveMediaMultiImei(data.item)"
          :tooltipText="$t('global.noMedia')"
          @click="openModalMedia(data.item)"
        />
        <span v-else-if="data.item.mediaStatus === 'NoMedia'" class="mc-status mc-status--red">
          {{ $t('global.action.notAvailable') }}
        </span>
        <span v-else-if="data.item.mediaStatus === 'InProgress'" class="mc-status mc-status--blue">
          {{ $t('global.action.uploadProcess') }}
        </span>
      </template>
      <template #cell(createdOn)="data">
        <div class="d-flex flex-column">
          <span>{{ momentDateShortMonth(data.item.createdOn) }}</span>
          <span class="text-muted">
            {{ momentTimeSeconds(data.item.createdOn) }} {{ getLabelTimezone() }}
          </span>
        </div>
      </template>
      <template #cell(falseAlert)="data">
        <span v-if="data.item?.isFalseAlert" class="false-alert-icon">
          <McBadge text="False Alert" variant="danger" type="status" />
        </span>
        <span v-else-if="data.item?.isResolved">
          <McBadge :text="$t('controlTower.detail.checked')" variant="primary" type="status" />
        </span>
        <span v-else>
          <McBadge :text="$t('controlTower.detail.notChecked')" variant="draft" type="status" />
        </span>
      </template>
      <template #cell(falseAlertReason)="data">
        {{ data.item.lastUserFeedback?.feedback ?? '-' }}
      </template>
      <template #cell(address)="data">
        <div class="mc-table__address-field">
          <div>
            <span @click="openGoogleMaps(data.item.latitude, data.item.longitude)">{{
              getAddress(data)
            }}</span>
            <u
              v-b-tooltip.hover
              :title="$t('global.field.copy')"
              @click="copyAddress(getAddress(data))"
            >
              <i class="mc-fa icon-outline_copy"></i>
            </u>
          </div>
        </div>
      </template>
      <template #cell(alertTrigger)="data">
        <template
          v-if="
            checkIsDmsAdas(data.item.alert.alertCategories) ||
            isFO(data.item.alert?.alertCategories, data.item.alertCode)
          "
        >
          {{ '-' }}
        </template>
        <template v-else-if="data.item.alertCode == 151">
          <span
            v-html="
              convertValue(
                data.item,
                data.value,
                data.item.alertCode,
                'alertTrigger',
                data.item.rawAlertId
              )
            "
          ></span>
        </template>
        <template v-else>
          <span
            v-html="
              convertValue(
                data.item,
                data.value,
                data.item.alertCode,
                'alertTrigger',
                data.item.rawAlertId
              )
            "
          ></span>
        </template>
      </template>
      <template #cell(alertData)="data">
        <template v-if="data.item.speed != null">
          {{ `${Number.parseInt(data.item.speed)} Km/${$t('global.time.hour')}` }}
        </template>
        <template
          v-else-if="
            checkIsDmsAdas(data.item.alert.alertCategories) ||
            isFO(data.item.alert?.alertCategories, data.item.alertCode)
          "
        >
          {{ '-' }}
        </template>
        <template v-else>
          <span v-if="data.item.alertCode == 15 || data.item.alertCode == 16">
            {{
              convertValue(
                data.item,
                data.item.formattedAlertData,
                data.item.alertCode,
                'alertData'
              ) || '-'
            }}
          </span>
          <span v-else-if="data.item.alertCode == 151">
            {{ convertValue(data.item, data.value, data.item.alertCode, 'alertData') }}
          </span>
          <span
            v-else
            v-html="convertValue(data.item, data.value, data.item.alertCode, 'alertData')"
          ></span>
        </template>
      </template>
      <template #cell(vehicle)="data">
        {{ data.item?.vehicle?.licensePlate ?? '-' }}
      </template>
      <template #cell(vehicleGroup)="data">
        <McChipList
          class="mc-table-notification__vechile-group-items"
          :items="getSortedVgroupItems(data.item?.vehicle?.vehicle2Vgroup)"
          :maxVisible="3"
          itemTextField="name"
          :hideToggle="true"
          :truncateTextLength="15"
        />
      </template>
      <template #cell(geofences)="data">
        <McChipList
          class="mc-table-notification__vechile-group-items"
          :items="data.item?.geofences?.map((g) => g.fenceName)"
          :maxVisible="3"
          :hideToggle="true"
          :truncateTextLength="10"
        />
      </template>
    </McGrapqlTable>
    <b-modal
      id="modal-unread"
      class="mc-modal"
      :title="$t('notification.text.markAllRead')"
      size="md"
      centered
    >
      <div class="card-body p-2">
        <p>{{ $t('notification.text.allImportantNotif') }}</p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.action.markRead')" @click="handleReadAll" />
        </div>
      </template>
    </b-modal>
    <b-modal
      id="modal-falsealert"
      class="mc-modal"
      title="Tandai sebagai False Alert ?"
      size="md"
      centered
    >
      <div class="card-body p-2">
        <label>
          {{ $t('notification.text.whyFalseAlert') }} ?
          <span class="text-danger">*</span>
        </label>
        <FormulateForm class="mc-notification-alert__false-suggest">
          <FormulateInput
            type="textarea"
            name="reason"
            :placeholder="$t('notification.text.giveOtherReasons')"
            validation="max:300,length"
            :validation-name="$t('notification.text.otherReasons')"
            error-behavior="live"
          />
          <div class="mt-1">
            <McButton variant="secondary" size="sm" :text="$t('notification.text.notValid')" />
            <McButton
              variant="secondary"
              size="sm"
              class="ms-1"
              :text="$t('notification.text.otherReason2')"
            />
          </div>
        </FormulateForm>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" text="Konfirmasi False Alert" @click="handleReadAll" />
        </div>
      </template>
    </b-modal>
    <b-modal
      id="modal-resolve"
      class="mc-modal"
      title="Tandai selesai diperiksa ?"
      size="md"
      centered
    >
      <div class="card-body p-2">
        <p>{{ `${selectedItems.length}` }} {{ $t('notification.text.notificationWill') }}</p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" text="Ya, selesai diperiksa" @click="handleReadAll" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import ModalFilter from './components/ModalFilterV2'
import McBadge from '@/components/McBadge'
import McChipList from '@/components/McChipList'
import {
  GET_RAW_ALERTS_WITH_ADDRESS,
  UPSERT_RAW_ALERTS_ACTION,
} from '@/services/api/driverscore/score'
import { GET_RAW_ALERT } from '@/services/api/fleet'
import { DEVICE_CODE_LIST } from '@/helpers/device-code'
import moment from 'moment'
import { dataDemoDms } from './lib/data-demo/dms'
import { dataDemoAdas } from './lib/data-demo/adas'
import { getGeolocation } from '@/services/api/geolocation'
import { GetAddressData } from '@/services/api/map'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'
import { NOTIFICATION_LIST_CODES } from '@/services/api/notification/general'
import { MixPanel } from '@/mixin/mixpanel'
import { Notification } from '@/mixin/notification'
import { dataDemoDriverBehavior } from './lib/data-demo/driver-behavior'
import { dataDemoGeneral } from './lib/data-demo/general'

export default {
  page: {
    title: 'Notifikasi',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McGrapqlTable,
    McFilterSelect,
    McButton,
    ModalFilter,
    McBadge,
    McChipList,
  },
  mixins: [Helper, MixPanel, Notification],
  apollo: {
    alertCodes: {
      query: NOTIFICATION_LIST_CODES,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            alertCode: this.getNotificationAlertCodes(),
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        const res = [...data.alertCodes.alertCodes]
        this.notifications = res
      },
    },
  },
  data() {
    return {
      jcCode: DEVICE_CODE_LIST.CONCOX_CAMERA,
      bsjCode: DEVICE_CODE_LIST.BSJ,
      vssHowenCode: DEVICE_CODE_LIST.VSS_HOWEN,
      reload: false,
      isImportant: false,
      isUrgent: false,
      isUrgentImportant: false,
      phaseOne: true,
      items: [],
      selectedItems: [],
      activeFilters: {},
      filterModal: {},
      fieldShow: JSON.parse(localStorage.getItem('notificationFields')),
      alertCodeActive: [],
      rawAlertsStateActive: [],
      addresses: {},
      rawFilters: [],
      isLoading: false,
      localFullObjects: {},
      currentPage: 1,
      currentLimit: 10,
      currentSearch: '',
      defaultPeriod: [
        moment().subtract(1, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
      ],
      period: [moment().subtract(1, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      pastDate: moment().subtract(6, 'months'),
      futureEnd: moment().add(6, 'months'),
      currentPastDate: moment().subtract(6, 'months'),
      currentFutureEnd: moment().add(6, 'months'),
      notifications: [],
      wsMessageHandler: null,

      /**
       * key is downloadId, value is data dvh
       * this Map is used for case Realtime check auto upload DMS/ADAS
       */
      mapDvhInProgress: {},
      formatValue: [
        {
          alertCode: [13, 14],
          format: [
            {
              type: 'null-value',
              symbol: '-',
            },
            {
              type: 'suffix',
              symbol: 'global.unit.liter',
            },
          ],
        },
        {
          alertCode: [
            2, 37, 36, 10, 7, 75, 1, 12, 4, 19, 5, 20, 21, 22, 3, 62, 63, 11, 94, 95, 117, 118,
          ],
          format: [
            {
              type: 'value',
              symbol: '-',
            },
          ],
        },
        {
          alertCode: [15, 16, 104],
          format: [
            {
              type: 'null-value',
              symbol: '-',
            },
            {
              type: 're-formatting',
              symbol: 'duration',
            },
          ],
        },
        {
          alertCode: [17, 18],
          format: [
            {
              type: 'null-value',
              symbol: '-',
            },
            {
              type: 'suffix',
              symbol: 'global.unit.celcius',
            },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    lang() {
      return this.$i18n.locale
    },
    title() {
      return this.$t('global.object.notification')
    },
    subTitle() {
      return this.$t('notification.subtitle')
    },
    storedFields() {
      const storedColumns = this.fieldShow.column
      const formatters = {
        'driver.fullname': (val) => {
          return val ?? '-'
        },
      }
      return storedColumns.map((field) => {
        if (formatters[field.key]) {
          return {
            ...field,
            formatter: formatters[field.key],
          }
        }
        return field
      })
    },
    fields() {
      if (this.fieldShow) {
        return this.storedFields
      } else {
        return [
          {
            key: 'alert',
            label: this.$t('global.object.notification'),
            isRedirectable: true,
            isLocked: true,
          },
          {
            key: 'media',
            isSortable: false,
            label: 'Media',
          },
          {
            key: 'vehicle',
            label: this.$t('global.object.licensePlate'),
            isSortable: false,
          },
          {
            key: 'vehicleGroup',
            label: this.$t('global.object.fleetGroup'),
            isSortable: false,
          },
          {
            key: 'createdOn',
            label: this.$t('global.eventTime'),
            isSortable: false,
          },
          {
            key: 'alertData',
            isSortable: false,
            label: this.$t('global.field.actual'),
          },
          {
            key: 'alertTrigger',
            isSortable: false,
            label: this.$t('global.object.limit'),
          },
          {
            key: 'address',
            label: this.$t('global.field.address'),
            isSortable: true,
          },
          {
            key: 'geofences',
            label: this.$t('report.report.geofence'),
            isSortable: false,
          },
          {
            key: 'falseAlert',
            label: this.$t('global.field.checkedStatus'),
            isSortable: true,
          },
          {
            key: 'falseAlertReason',
            label: this.$t('global.object.note'),
            isSortable: true,
          },
        ]
      }
    },
    query() {
      return {
        get: async (filter) => {
          return this.getData(filter)
        },
      }
    },
    getNotificationDescription() {
      return (item, value) => {
        if (item.alertCode === 118) {
          return item.alertFlag
            ? this.$t('notification.alert.onlineVehicle')
            : this.$t('notification.alert.offlineVehicle')
        }
        return this.$i18n.locale === 'id' ? value.descriptionIdn : value.descriptionEng
      }
    },
    checkIsDmsAdas() {
      return (alertCategories) => {
        return alertCategories?.some(({ alertCategoryId }) => this.isDmsAdas(alertCategoryId))
      }
    },
    isFO() {
      return (alertCategories, alertCode) => {
        return (
          alertCode !== 151 && alertCategories?.some(({ alertCategoryId }) => alertCategoryId === 6)
        )
      }
    },
    periodeRangesInLast7Days() {
      const startDate = moment(this.period[0], 'YYYY-MM-DD')
      const endDate = moment(this.period[1], 'YYYY-MM-DD')

      const today = moment().startOf('day')
      const last7DaysStart = today.clone().subtract(7, 'days')
      const last7DaysEnd = today.clone().subtract(1, 'days')

      const overlaps =
        startDate.isSameOrBefore(last7DaysEnd) && endDate.isSameOrAfter(last7DaysStart)

      return overlaps || startDate.isSame(today, 'day') || endDate.isSame(today, 'day')
    },
    isSalesDemo() {
      const email = this.$store.state.identity.whoami.email
      const demoStaging =
        email === 'mceasy-demo@vsms.co.id' &&
        (window.location.host === 'vsms2-staging.mceasy.cloud' ||
          window.location.host === 'localhost:8080' ||
          window.location.host === 'localhost:3000')
      return (
        (demoStaging || window.location.host === 'demo.mceasy.com') && this.periodeRangesInLast7Days
      )
    },
    dataDemo() {
      const transformData = (dataset, { isDmsAdas }) => {
        return dataset.map((data) => {
          const alertCreatedOn = `${this.period[0]}T${data.createdOn.split('T')[1]}`
          const prevAlertDateTime = this.generateRandomDateTimes(
            alertCreatedOn,
            data.previousAlerts.length
          )

          return {
            ...data,
            address: '',
            createdOn: alertCreatedOn,
            previousAlerts: data.previousAlerts.map((alert, index) => ({
              ...(index === 0 ? { ...data } : { ...alert }),
              createdOn: prevAlertDateTime[index],
            })),
            isDmsAdas,
            isDemo: true,
            media: data.media.map((m) => ({
              ...m,
              startTime: alertCreatedOn,
              vehicleId: data.vehicleId,
            })),
            id: `${new Date(alertCreatedOn).getTime()}-${data.vehicle.id}-${data.alertCode}`,
          }
        })
      }

      const dms = transformData(dataDemoDms, { isDmsAdas: true })
      const adas = transformData(dataDemoAdas, { isDmsAdas: true })
      const driverBehavior = transformData(dataDemoDriverBehavior, { isDmsAdas: false })
      const general = transformData(dataDemoGeneral, { isDmsAdas: false })

      return [...dms, ...adas, ...driverBehavior, ...general]
    },
    vehicleId() {
      return Number(this.$route.query?.id)
    },
    vehicleObject() {
      return this.vehicleId
        ? {
            licensePlate: [
              {
                id: this.vehicleId,
                licensePlate: this.$route.query.licensePlate,
              },
            ],
          }
        : this.localFullObjects
    },
    illustrationPath() {
      return require('@/assets/images/illustration/saas/no-notification.png')
    },
    makeShortcuts() {
      return [
        { text: this.$t('global.time.today'), onClick: () => [new Date(), new Date()] },
        {
          text: this.$t('global.time.yesterday'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24)
            endDate.setTime(endDate.getTime() - 3600 * 1000 * 24)
            startDate.setHours(0, 0, 0, 0)
            endDate.setHours(23, 59, 59, 999)
            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last3Days'),
          onClick: () => {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 72)
            return [date, new Date()]
          },
        },
      ].map((shortcut) => ({ ...shortcut, value: shortcut.onClick }))
    },

    // case multi imei
    isHaveMediaMultiImei() {
      return (alert) => {
        // case 1 : if there is only 1 device and device is cam in one family
        const isParentCam =
          alert.vehicle?.sensor?.some((sensor) => sensor.sensorCode === 35) &&
          (this.jcCode.includes(alert.vehicle.device?.deviceCode) ||
            this.bsjCode.includes(alert.vehicle.device?.deviceCode) ||
            this.vssHowenCode.includes(alert.vehicle.device?.deviceCode))

        // case 2 : if there is child cam
        // implement in multi imei, if the parent gps have children cam
        const isChildCam = !!alert.vehicle.children?.length || !!alert.childVehicleId

        // case 3 : check to field media
        // if media is exist (mean more than 0 item), assume raw alert have media that source is device cam
        const isHaveMedia =
          alert.media?.length > 0 || (alert.formattedAlertData?.startsWith('https') ?? false)

        return isParentCam || isChildCam || isHaveMedia
      }
    },
    checkIsAlertRequested() {
      return (alert) => {
        const vehicleIdCam = alert?.childVehicleId || alert?.vehicleId
        const alertCreatedOnTime = new Date(alert.createdOn).getTime()

        if (Number.isNaN(alertCreatedOnTime)) return false

        return this.$store.state.notification.dataDvhRequested.some(
          (dvh) =>
            dvh.vehicleId === vehicleIdCam &&
            new Date(dvh.startTime).getTime() === alertCreatedOnTime
        )
      }
    },
    checkIsAutoUploadSuccessfull() {
      return (alert) => {
        const dvhI = alert.media.find((m) => m.status === 'I')
        if (!dvhI) return null
        const mapDvh = this.mapDvhInProgress[dvhI.downloadId]
        if (!mapDvh) return null
        if (mapDvh.status === 'S') return true
        if (mapDvh.status === 'F') return false
        return null
      }
    },
    hasRuleNameScope() {
      return this.hasAccess('e4J-alert-rule-name-as-title:read')
    },
    hasScopeNewModalMedia() {
      return this.hasAccess('b3t-trackvision-new-modal-media-alert:read')
    },
  },
  watch: {
    isUrgentImportant(newValue) {
      localStorage.setItem('notificationIsUrgentImportant', String(newValue))
    },
  },
  mounted() {
    this.track('Open Notification History / Log')

    const savedIsUrgentImportant = localStorage.getItem('notificationIsUrgentImportant')
    if (savedIsUrgentImportant !== null) {
      this.isUrgentImportant = savedIsUrgentImportant === 'true'
    }

    let filters = [
      [
        {
          key: 'statusCheck',
          values: ['unresolved', 'resolved', 'falseAlert'],
        },
        {
          key: 'alertCode',
          values: [],
        },
        {
          key: 'vehicleCategory',
          values: [],
        },
        {
          key: 'vehicleId',
          values: [],
        },
        {
          key: 'driverId',
          values: [],
        },
        {
          key: 'shouldHaveMedia',
          values: false,
        },
        {
          key: 'isRead',
          values: false,
        },
        {
          key: 'isImportant',
          values: false,
        },
      ],
    ]
    const savedState = this.$route.query.state ? this.decodeState(this.$route.query.state) : null

    const savedFilters = savedState?.f
      ? savedState.f
      : JSON.parse(localStorage.getItem('notification-filters'))?.filter(
          (item) => item.key !== 'period'
        )
    const savedFullObjects = JSON.parse(localStorage.getItem('notification-full-objects'))

    if (savedState?.sd && savedState?.ed) {
      this.period = [savedState.sd, savedState.ed]
    }
    if (savedState?.p) this.currentPage = savedState.p
    if (savedState?.l) this.currentLimit = savedState.l
    if (savedState?.s) this.currentSearch = savedState.s

    if (this.vehicleId) {
      this.isImportant = Boolean(this.$route.query.isImportant)
      const unresolved = Boolean(this.$route.query.isUnresolved)
      filters = [
        { key: 'vehicleId', values: [this.vehicleId] },
        { key: 'isImportant', values: this.isImportant },
        {
          key: 'statusCheck',
          values: unresolved ? ['unresolved'] : ['resolved', 'unresolved', 'falseAlert'],
        },
      ]
    } else if (savedFilters) {
      filters = savedFilters
      this.localFullObjects = savedFullObjects
    }
    this.handleApplyFilters(filters)
    this.setupWsDvh()
  },

  methods: {
    disabledRange: function (date) {
      let limit = new Date()
      return (
        date <= this.pastDate ||
        date >= this.futureEnd ||
        date <= this.currentPastDate ||
        date >= this.currentFutureEnd ||
        date > limit
      )
    },
    pickDate(date) {
      const maxDate = 3
      this.currentPastDate = new Date(date.getTime() - 24 * maxDate * 3600 * 1000)
      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000) == new Date()) {
        this.currentFutureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.currentFutureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },
    closePicker() {
      this.currentPastDate = moment().subtract(6, 'months')
      this.currentFutureEnd = moment().add(6, 'months')
    },
    unreadNotification(item, type) {
      if (!item || type !== 'row') return
      if (
        !item.isRead &&
        (item.isImportant || (item.isUrgent && this.hasAccess(['e4e-alert-center-urgent:read'])))
      )
        return 'unread-notification'
    },
    async fetchAddresses(res) {
      const useCache = this.isUsingCache()
      const coords = []
      const tempAddresses = { ...this.addresses }
      res.data.rawAlerts.rawAlerts.data.forEach((item) => {
        const lat = Number(item.latitude)
        const lon = Number(item.longitude)
        if (tempAddresses[lat + '&' + lon] === undefined) coords.push({ lat, lon })
      })
      if (coords.length > 0) {
        if (useCache) {
          const addresses = await getGeolocation(coords)
          for (const [key, value] of addresses) {
            tempAddresses[key.replace(',', '&')] = value.name
          }
        } else {
          await Promise.all(
            coords.map(async (coord) => {
              const res = await GetAddressData(coord.lat, coord.lon)
              tempAddresses[res.lat + '&' + res.lon] = res.display_name
            })
          )
        }
      }
      this.addresses = tempAddresses
    },
    async fetchAddressesDemo(data) {
      const useCache = this.isUsingCache()
      const coords = []
      const tempAddresses = { ...this.addresses }
      data.forEach((item) => {
        const lat = Number(item.latitude)
        const lon = Number(item.longitude)
        if (tempAddresses[lat + '&' + lon] === undefined) coords.push({ lat, lon })
      })
      if (coords.length > 0) {
        if (useCache) {
          const addresses = await getGeolocation(coords)
          for (const [key, value] of addresses) {
            tempAddresses[key.replace(',', '&')] = value.name
          }
        } else {
          await Promise.all(
            coords.map(async (coord) => {
              const res = await GetAddressData(coord.lat, coord.lon)
              tempAddresses[res.lat + '&' + res.lon] = res.display_name
            })
          )
        }
      }
      this.addresses = tempAddresses
    },
    getAddress(data) {
      return this.addresses[data.item.latitude + '&' + data.item.longitude]
    },
    openGoogleMaps(lat, lng) {
      const url = `https://www.google.com/maps?q=${lat},${lng}`
      window.open(url, '_blank')
    },
    copyAddress(text) {
      navigator.clipboard.writeText(text)
      this.$mcToast.success(this.$t('global.action.copySuccess'))
    },
    async getData(filter) {
      this.isLoading = false
      let filterDate = {
        startDate: moment(`${this.period[0]} 00:00:00`).toISOString(),
        endDate: moment(`${this.period[1]} 23:59:59`).toISOString(),
      }
      let filterAlerts = []
      let filterStatus = {}
      let filterSourceDeviceType = {}
      let otherFilters = {}
      if (Object.keys(this.activeFilters).length > 0) {
        Object.keys(this.activeFilters).forEach((key) => {
          let filterValue = this.activeFilters[key]
          if (key === 'alertCode') {
            filterValue.map((item) => filterAlerts.push(item))
          } else if (key === 'isImportant' || key === 'shouldHaveMedia') {
            otherFilters[key] = filterValue ? true : undefined
            if (key === 'isImportant') {
              this.isImportant = filterValue
            }
          } else if (key === 'isUrgent') {
            if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
              otherFilters[key] = filterValue ? true : undefined
              this.isUrgent = filterValue
            }
          } else if (key === 'isRead') {
            otherFilters[key] = filterValue ? false : undefined
          } else if (key === 'statusCheck') {
            const hasResolved = filterValue.includes('resolved')
            const hasUnresolved = filterValue.includes('unresolved')
            const hasFalseAlert = filterValue.includes('falseAlert')
            filterStatus = {
              isResolved:
                hasResolved && hasUnresolved
                  ? undefined
                  : hasResolved
                  ? true
                  : hasUnresolved
                  ? false
                  : undefined,
              isFalseAlert:
                hasResolved && hasUnresolved && hasFalseAlert
                  ? undefined
                  : hasResolved && hasUnresolved
                  ? false
                  : hasResolved && hasFalseAlert
                  ? undefined
                  : hasUnresolved && hasFalseAlert
                  ? true
                  : hasFalseAlert
                  ? true
                  : false,
            }
          } else if (key === 'sourceDeviceType') {
            const hasCam = filterValue.includes('CAM')
            const hasGps = filterValue.includes('GPS')
            filterSourceDeviceType = {
              sourceDeviceType:
                hasCam && hasGps ? undefined : hasCam ? 'CAM' : hasGps ? 'GPS' : undefined,
            }
          } else {
            otherFilters[key] = filterValue
          }
        })
      }
      const alertCode =
        filterAlerts.length !== 0
          ? { alertCode: filterAlerts }
          : { alertCode: this.getAlertOptions.flatMap((item) => item.alertCode) }
      this.alertCodeActive = alertCode.alertCode
      this.isLoading = false
      if (this.isSalesDemo) {
        const dataDemo = this.dataDemo.map((data, index) => {
          const alertData = data.alertData
          const vehicleId = data.vehicleId
          const urlDemo = process.env.VUE_APP_DEMO_ASSET_URL + '/' + vehicleId + '/' + alertData
          return {
            ...data,
            formattedAlertData: data.isDmsAdas ? urlDemo ?? null : data.formattedAlertData,
            address: this.addresses[`${data.latitude}&${data.longitude}`] ?? '',
          }
        })

        const endpointRes = await this.$apollo.query({
          query: GET_RAW_ALERTS_WITH_ADDRESS,
          fetchPolicy: 'no-cache',
          variables: {
            includeMediaStatus: true,
            includeMedia: true,
            filter: {
              ...filter,
              ...alertCode,
              ...otherFilters,
              ...filterStatus,
              orderBy: JSON.stringify({ createdOn: 'desc' }),
              startDate:
                filterDate.startDate ?? moment(`${this.defaultPeriod[0]} 00:00:00`).toISOString(),
              endDate:
                filterDate.endDate ?? moment(`${this.defaultPeriod[1]} 23:59:59`).toISOString(),
              pagination: {
                page: 1,
                show: this.getFeatureFlagData('demo_total_alert').value,
              },
            },
          },
        })
        const endpointData = endpointRes.data.rawAlerts.rawAlerts.data.map((item) => ({
          id: `${new Date(item.createdOn).getTime()}-${item.vehicle.id}-${item.alertCode}`,
          ...item,
        }))

        this.rawAlertsStateActive = endpointRes.data.rawAlerts.rawAlerts.data.map((data) => {
          return data.rawAlertId
        })
        await this.fetchAddresses(endpointRes)

        const combinedData = [...dataDemo, ...endpointData]

        const filteredData = combinedData.filter((item) =>
          alertCode.alertCode.includes(item.alertCode)
        )

        const result = {
          data: filteredData.slice(
            (filter.pagination.page - 1) * filter.pagination.show,
            filter.pagination.show * filter.pagination.page
          ),
          pagination: {
            page: filter.pagination.page,
            show: filter.pagination.show,
            total: filteredData.length,
          },
        }
        this.items = combinedData

        return result
      } else {
        const res = await this.$apollo.query({
          query: GET_RAW_ALERTS_WITH_ADDRESS,
          fetchPolicy: 'no-cache',
          variables: {
            includeMediaStatus: true,
            includeMedia: true,
            filter: {
              ...filter,
              ...alertCode,
              ...otherFilters,
              ...filterStatus,
              ...filterSourceDeviceType,
              orderBy: JSON.stringify({ createdOn: 'desc' }),
              startDate:
                filterDate.startDate ?? moment(`${this.defaultPeriod[0]} 00:00:00`).toISOString(),
              endDate:
                filterDate.endDate ?? moment(`${this.defaultPeriod[1]} 23:59:59`).toISOString(),
            },
          },
        })
        const data = res.data.rawAlerts.rawAlerts.data.map((item) => ({
          id: `${new Date(item.createdOn).getTime()}-${item.vehicle.id}-${item.alertCode}`,
          ...item,
        }))
        this.items = data
        this.rawAlertsStateActive = res.data.rawAlerts.rawAlerts.data.map((data) => {
          return data.rawAlertId
        })
        await this.$nextTick()

        this.setMapAlertDvhInProgress()

        await this.fetchAddresses(res)

        if (this.hasRuleNameScope) {
          data.forEach((item) => {
            return {
              ...data,
              alertRules: this.filterAlertRules(item.alertRuleIds),
            }
          })
        }

        return {
          ...res.data.rawAlerts.rawAlerts,
          data: data,
        }
      }
    },

    filterAlertRules(alertRuleIds) {
      const storedAlertRuleIds = this.$store.state.notification.alertRuleObjs
      if (!alertRuleIds || !Array.isArray(alertRuleIds)) {
        return []
      }

      const filterAlertRuleList = alertRuleIds
        .map((id) => storedAlertRuleIds[id])
        .filter((rule) => rule)

      return filterAlertRuleList
    },
    handleSelected(selected) {
      this.selectedItems = this.items.filter((item) => {
        return selected[item.id] === true
      })
    },
    async handleReadAll() {
      this.reload = true
      const input = {
        actionType: 'Read',
        data: null,
        selectionMode: 'All',
        dateRange: {
          start: this.period[0],
          end: this.period[1],
        },
      }
      const res = await this.$apollo.mutate({
        mutation: UPSERT_RAW_ALERTS_ACTION,
        variables: {
          input: input,
        },
      })
      if (!res.data.upsertRawAlertsAction.isSuccessful) {
        this.$mcToast.error(res.data.upsertRawAlertsAction.message)
        this.reload = false
        return
      }
      this.$mcToast.success(this.$t('notification.text.successRead'))
      this.$bvModal.hide('modal-unread')
      this.reload = false
    },
    async readItem(item) {
      const input = {
        actionType: 'Read',
        data: null,
        rawAlertId: [item.rawAlertId],
        selectionMode: 'Selection',
      }
      const res = await this.$apollo.mutate({
        mutation: UPSERT_RAW_ALERTS_ACTION,
        variables: {
          input: input,
        },
      })
      if (!res.data.upsertRawAlertsAction.isSuccessful) {
        this.$mcToast.error(res.data.upsertRawAlertsAction.message)
        return
      }
      this.$mcToast.success(this.$t('notification.text.successRead'))
      this.items.forEach((alert) => {
        if (alert.rawAlertId === item.rawAlertId) {
          alert.isRead = true
        }
      })
    },
    async rowClicked(item) {
      if (item.isDemo) {
        localStorage.setItem('data-demo-detail', JSON.stringify(item))
      }
      if (!item.isRead && item.isImportant) {
        this.readItem(item)
      }
      localStorage.setItem('drivers-detail-map', JSON.stringify(item))
      const createdOn = new Date(item.createdOn).getTime()
      const id = item.rawAlertId
        ? `${item.rawAlertId}`
        : `${item.alertCode}-${item.vehicle.id}-${createdOn}-${item.alertFlag}`
      this.$router.push({
        name: 'Notification Detail',
        params: {
          id: id,
        },
        query: { ...(item.isDemo ? { isDemo: true } : {}) },
      })
    },
    getNotificationUrl(item) {
      const createdOn = new Date(item.createdOn).getTime()
      const id = item.rawAlertId
        ? `${item.rawAlertId}`
        : `${item.alertCode}-${item.vehicle.id}-${createdOn}-${item.alertFlag}`
      const route = this.$router.resolve({
        name: 'Notification Detail',
        params: { id: id },
        query: { ...(item.isDemo ? { isDemo: true } : {}) },
      })
      return route.href
    },
    handleFilterModal(filters) {
      this.filterModal = {
        vehicleCategory: this.findFilterValue(filters, 'vehicleCategory') || [],
        alertCode: this.findFilterValue(filters, 'alertCode') || [],
        vehicleId: this.findFilterValue(filters, 'vehicleId') || [],
        driverId: this.findFilterValue(filters, 'driverId') || [],
        statusCheck: this.findFilterValue(filters, 'statusCheck') || [],
        isImportant: this.findFilterValue(filters, 'isImportant'),

        isUrgent: this.hasAccess(['e4e-alert-center-urgent:read'])
          ? this.findFilterValue(filters, 'isUrgent')
          : undefined,
        shouldHaveMedia: this.findFilterValue(filters, 'shouldHaveMedia'),
        isRead: this.findFilterValue(filters, 'isRead'),
        sourceDeviceType: this.findFilterValue(filters, 'sourceDeviceType') || [],
      }
    },
    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)
        if (filter.values === null || filter.values === undefined) return
        if (Array.isArray(filter.values) && filter.values.length === 0) return
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    handleLocalFullObjects(value) {
      this.localFullObjects = value
      localStorage.setItem('notification-full-objects', JSON.stringify(value))
    },
    handleApplyFilters(value) {
      this.rawFilters = value
      localStorage.setItem('notification-filters', JSON.stringify(value))

      const importantFilter = this.findFilterValue(value, 'isImportant')
      const urgentFilter = this.hasAccess(['e4e-alert-center-urgent:read'])
        ? this.findFilterValue(value, 'isUrgent')
        : undefined

      this.isImportant = !!importantFilter
      if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
        this.isUrgent = !!urgentFilter
        this.isUrgentImportant = !!(importantFilter && urgentFilter)
      } else {
        this.isUrgentImportant = !!importantFilter
      }

      this.handleFilterModal(value)
      this.handleChangeActiveFilters(value)
      this.updateUrlQuery()
    },
    applyDateFilters(value) {
      this.period = value
      this.reload = true
      this.$nextTick(() => {
        this.reload = false
      })
      this.updateUrlQuery()
    },
    handleTableUpdate(key, value) {
      if (key === 'page') this.currentPage = value
      if (key === 'limit') this.currentLimit = value
      if (key === 'search') this.currentSearch = value
      this.updateUrlQuery()
    },
    updateUrlQuery() {
      const state = {}
      if (this.currentPage !== 1) state.p = this.currentPage
      if (this.currentLimit !== 10) state.l = this.currentLimit
      if (this.currentSearch !== '') state.s = this.currentSearch
      if (this.rawFilters && this.rawFilters.length > 0) state.f = this.rawFilters
      if (this.period[0] !== this.defaultPeriod[0]) state.sd = this.period[0]
      if (this.period[1] !== this.defaultPeriod[1]) state.ed = this.period[1]

      const encodedState = Object.keys(state).length > 0 ? this.encodeState(state) : undefined
      const query = { ...this.$route.query, state: encodedState }

      if (!encodedState) delete query.state

      if (encodedState === this.$route.query.state) return

      this.$router.replace({ query }).catch(() => {})
    },
    encodeState(state) {
      try {
        const json = JSON.stringify(state)
        return btoa(
          encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (match, p1) => {
            return String.fromCharCode('0x' + p1)
          })
        )
      } catch (e) {
        return ''
      }
    },
    decodeState(encoded) {
      try {
        const json = decodeURIComponent(
          atob(encoded)
            .split('')
            .map((c) => {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            })
            .join('')
        )
        return JSON.parse(json)
      } catch (e) {
        return null
      }
    },
    handleImportant() {
      if (this.rawFilters.length === 0) {
        this.rawFilters.push({ key: 'period', value: this.period })
        if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
          this.rawFilters.push({ key: 'isUrgent', values: this.isUrgentImportant })
        }
        this.rawFilters.push({ key: 'isImportant', values: this.isUrgentImportant })
      }

      const importantFilterIndex = this.rawFilters.findIndex(
        (filter) => filter.key === 'isImportant'
      )
      if (importantFilterIndex >= 0) {
        this.rawFilters[importantFilterIndex].values = this.isUrgentImportant
      } else {
        this.rawFilters.push({ key: 'isImportant', values: this.isUrgentImportant })
      }

      if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
        const urgentFilterIndex = this.rawFilters.findIndex((filter) => filter.key === 'isUrgent')
        if (urgentFilterIndex >= 0) {
          this.rawFilters[urgentFilterIndex].values = this.isUrgentImportant
        } else {
          this.rawFilters.push({ key: 'isUrgent', values: this.isUrgentImportant })
        }
      }

      this.handleApplyFilters(this.rawFilters)
    },
    handleFieldShow(fields) {
      localStorage.setItem('notificationFields', JSON.stringify({ column: fields }))
    },
    async openModalMedia(item) {
      if (this.isSalesDemo && item.isDemo) {
        const detailNotification = { ...item, falseAlert: false }
        this.$store.commit('notification/setDetailNotification', detailNotification)
        this.$store.commit(
          'notification/setListNotificationMedia',
          item.media.map((media) => ({
            ...media,
            url:
              process.env.VUE_APP_DEMO_ASSET_URL + '/' + item.vehicleId + '/' + media.mediaFilename,
          }))
        )
        await this.$nextTick()
        this.$bvModal.show('modal-alert-media')
        return
      }
      if (!item.isRead && item.isImportant) {
        this.readItem(item)
      }
      const res = await this.$apollo.query({
        query: GET_RAW_ALERT,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            rawAlertId: item.rawAlertId,
          },
        },
      })

      const rawAlertData = res.data.rawAlert.rawAlert
      const dvhData = res.data.rawAlert.rawAlert.media
      const falseAlert = rawAlertData.lastUserFeedback
      const detailNotification = { ...rawAlertData, falseAlert }

      this.$store.commit('notification/setDetailNotification', detailNotification)
      this.$store.commit('notification/setListNotificationMedia', dvhData)
      await this.$nextTick()
      this.$bvModal.show('modal-alert-media')
    },
    showOnboarding() {
      EventBus.$emit('show-onboarding')
    },
    convertValue(alert, value, alertCode, type, alertId = null) {
      let result = value
      if (alertCode == 15 || alertCode == 16) {
        value = value * 60
      }
      if (alertCode == 151) {
        const alertDataValue = alert?.alertData
        if (!alertDataValue) return '-'

        const splitData = alertDataValue.toString().split('|')

        if (type == 'alertTrigger') {
          if (splitData.length >= 4) {
            const thirdValue = splitData[3]?.trim()
            const lastValue = splitData[splitData.length - 1]?.trim()

            if (thirdValue === 'Multi DO') {
              return 'Multi DO'
            } else {
              return (
                (Number.isInteger(lastValue)
                  ? lastValue
                  : parseFloat(lastValue).toFixed(1).replace(/\.0$/, '')) + ' ℃' || '-'
              )
            }
          }
          return '-'
        }

        if (type == 'alertData') {
          if (splitData.length >= 4) {
            return splitData[4]?.trim().replace(/\.0$/, '') + ' ℃' || '-'
          }
          return '-'
        }
      }
      if (alertCode == 117) {
        if (type == 'alertTrigger' && !this.hasAccess(['e4k-alert-driving-time-threshold:read'])) {
          return `4 ${this.$t('global.text.time.hour')}`
        }
        if (type == 'alertData' || type == 'alertTrigger') return this.minuteToHour(value)
      }
      if (alertCode == 118) {
        if (type == 'alertTrigger')
          return value ? `${value} ${this.$t('global.time.minutes')}` : '-'
      }
      if (alertCode == 126) {
        return '-'
      }
      if (alertCode == 8) {
        if (type == 'alertData') {
          if (!value) return '-'
          const speed = value.toString().split('|')[0].trim()
          const duration = value.toString().split('|')[1].trim()
          if (this.hasAccess(['e4d-alert-overspeed-threshold-duration:read']))
            return this.sanitizeHTML(
              `${speed} Km/${this.$t('global.time.hours')}<br/>${duration} ${this.$t(
                'global.time.seconds'
              )}`
            )
          else return this.sanitizeHTML(`${speed} Km/${this.$t('global.time.hours')}<br/>`)
        }
        if (type == 'alertTrigger') {
          if (!value) return '-'

          const alertDataValue = alert?.alertData

          if (!alertDataValue) return '-'

          const durationLimit = alertDataValue.toString().split('|')[3]?.trim() || 60

          if (this.hasAccess(['e4d-alert-overspeed-threshold-duration:read']))
            return this.sanitizeHTML(
              `${value} Km/${this.$t('global.time.hours')}<br/>${durationLimit} ${this.$t(
                'global.time.seconds'
              )}`
            )
          else return this.sanitizeHTML(`${value} Km/${this.$t('global.time.hours')}<br/>`)
        }
      }
      const format = this.formatValue.find((item) => item.alertCode.includes(alertCode))
      if (format) {
        format.format.forEach((item) => {
          if (item.type == 'value') result = item.symbol
          else {
            if (value == null || typeof value === 'undefined' || value == '') {
              if (item.type == 'null-value') result = item.symbol
            } else {
              if (item.type == 'suffix') result = `${value} ${this.$t(item.symbol)}`
              else if (item.type == 're-formatting') {
                if (item.symbol == 'duration') {
                  const timeObject = this.convertTimeToObject(value)
                  result = ''
                  if (timeObject.day) {
                    result += `${timeObject.day} ${
                      timeObject.day > 1 ? this.$t('global.time.days') : this.$t('global.time.day')
                    } `
                  }
                  if (timeObject.hour) {
                    result += `${timeObject.hour} ${
                      timeObject.hour > 1
                        ? this.$t('global.time.hours')
                        : this.$t('global.time.hour')
                    } `
                  }
                  if (timeObject.minute) {
                    result += `${timeObject.minute} ${
                      timeObject.minute > 1
                        ? this.$t('global.time.minutes')
                        : this.$t('global.time.minute')
                    } `
                  }
                  if (timeObject.second) {
                    result += `${timeObject.second} ${
                      timeObject.second > 1
                        ? this.$t('global.time.seconds')
                        : this.$t('global.time.second')
                    }`
                  }
                }
              }
            }
          }
        })
      }
      return result
    },
    async setupWsDvh() {
      this.wsMessageHandler = this.handleWsMessage.bind(this)
      await this.$store.dispatch('ws/registerMessageHandler', this.wsMessageHandler)
    },

    setMapAlertDvhInProgress() {
      this.items.forEach((item) => {
        item.media.forEach((media) => {
          if (media.status === 'I') {
            this.$set(this.mapDvhInProgress, media.downloadId, media)
          }
        })
      })
    },

    handleWsMessage(message) {
      const dataWs = typeof message === 'string' ? JSON.parse(message) : message
      if (dataWs.type?.startsWith('dvh-')) {
        this.handleMessageDvhAutoUpload(dataWs)
      }
    },

    // This function to handler ws from auto upload from kernel
    handleMessageDvhAutoUpload(dataWs) {
      const dataDvh = dataWs.payload
      if (
        !!this.mapDvhInProgress[dataDvh.downloadId] &&
        (dataDvh.status === 'F' || dataDvh.status === 'S')
      ) {
        this.$set(this.mapDvhInProgress, dataDvh.downloadId, dataDvh)
        // if (dataDvh.status === 'S') {
        //   this.$mcToast.success(this.$t('notification.text.requestVideoSuccess'))
        // } else if (dataDvh.status === 'F') {
        //   this.$mcToast.error(this.$t('notification.text.requestVideoFailed'))
        // }
      }
    },

    generateRandomDateTimes(baseDate, length) {
      const base = new Date(baseDate)
      const result = [base.toISOString()]

      let current = new Date(base)

      for (let i = 1; i < length; i++) {
        const offsetMinutes = Math.floor(Math.random() * 60) + 1
        current = new Date(current.getTime() - offsetMinutes * 60 * 1000)
        result.push(current.toISOString())
      }

      return result
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-table__clickable-field {
  font-weight: 600;
  color: $mep-primary-400;
  cursor: pointer;
  display: flex;
  align-items: center;
  img {
    width: 20px;
    height: 20px;
  }
  .urgent-icon {
    position: relative;
    margin-right: 10px;

    i {
      position: absolute;
      z-index: 2;
      left: 0;
      right: 0;
      margin: 0 auto;
      text-align: center;
      font-size: 9px;
      top: 4px;
      color: $white;
    }

    &.counter {
      &:before {
        content: '';
        background-color: $red;
        width: 3px;
        height: 13px;
        position: absolute;
        top: 2px;
        left: 8px;
      }
    }

    img {
      filter: brightness(0) saturate(100%) invert(16%) sepia(72%) saturate(4723%) hue-rotate(1deg)
        brightness(105%) contrast(103%);
    }
  }
  .important-icon {
    position: relative;
    margin-right: 10px;
    i {
      position: absolute;
      z-index: 2;
      left: 0;
      right: 0;
      margin: 0 auto;
      text-align: center;
      font-size: 9px;
      top: 4px;
      color: $white;
    }

    &.counter {
      &:before {
        content: '';
        background-color: $yellow;
        width: 3px;
        height: 13px;
        position: absolute;
        top: 2px;
        left: 8px;
      }
    }

    img {
      filter: brightness(0) saturate(100%) invert(49%) sepia(46%) saturate(1692%) hue-rotate(2deg)
        brightness(108%) contrast(104%);
    }
  }

  .mc-notification-log_notification-name {
    margin-left: 31.5px;

    &.__important {
      margin-left: 0px;
    }
  }
}
.mc-table__address-field {
  position: relative;

  div {
    width: 320px;
    white-space: normal;
    display: flex;
    justify-content: space-between;
  }

  span {
    cursor: pointer;
    margin-right: 10px;
  }

  u {
    font-size: 18px;
    cursor: pointer;
    text-decoration: none;
  }
}
:deep(.unread-notification) {
  td {
    background-color: #deeafc;
  }
}

:deep(.mc-notification-alert__false-suggest) {
  .formulate-input {
    padding: 0;
  }
  .mc-btn__sm {
    padding: 2px 5px;
    font-size: 9px;
    background-color: $cyan-100;
    color: $cyan-400;
    border: 0;

    &:hover {
      background-color: $cyan-200;
      color: $blue-500;
    }
  }
}
.mc-btn__recommendation {
  display: flex;
  align-items: center;
  padding: 1rem;
  img {
    width: 80px;
    margin-right: 8px;
  }
  span {
    display: block;
    color: $teal-300;
  }
}

.mc-table-notification__vechile-group-items {
  :deep(.chip-list) {
    flex-wrap: nowrap !important;
  }
}
</style>
